/**
 * Sprint 3b — 6 Mrt 2026 — SumUp-stijl SCHERM-WEERGAVE voor factuurregels.
 *
 * Rustige lay-out per regel — geen card-borders of chip-lijst:
 *   Hoofdregel : omschrijving (bold, links) · qty × unit_price klein grijs
 *                onder omschrijving · line_total groot vet rechts
 *   Detailregel: één subtiele grijze regel met max 4 relevante metadata:
 *                aantal + eenheid · Btw X% · bestemming · W-nummer/customer_ref
 *
 * Regels: geen chip-boxes, geen lege labels, geen dubbele info die al in de
 * description staat. Lange omschrijvingen wrappen netjes. Bedrag blijft
 * rechts uitgelijnd.
 *
 * ⚠️ FACTUUR PDF WORDT NIET AANGERAAKT — dit is puur de React-schermweergave.
 */
import React from "react";

function fmtCurrency(value, currency = "EUR") {
  const num = Number(value || 0);
  return `${currency} ${num.toFixed(2).replace(".", ",")}`;
}

function fmtQty(qty) {
  const n = Number(qty || 0);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(".", ",").replace(/,?0+$/, "");
}

// Case-insensitive substring-check (dedup tegen description)
function includedIn(haystack, needle) {
  if (!haystack || !needle) return false;
  const norm = (s) => String(s).toLowerCase().replace(/\s+/g, " ").trim();
  return norm(haystack).includes(norm(needle));
}

export default function InvoiceLineCards({ invoice }) {
  const lines = invoice?.lines || [];
  const currency = invoice?.currency || "EUR";

  if (lines.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic p-3 border border-gray-200 bg-gray-50"
           data-testid="invoice-line-cards-empty">
        Geen factuurregels.
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100" data-testid="invoice-line-cards">
      {lines.map((l, i) => {
        const qty = Number(l.quantity || 0);
        const unitPrice = Number(l.unit_price || 0);
        const lineTotal = Number(l.line_total ?? qty * unitPrice);
        const unit = l.unit || "";
        const description = l.description || "—";

        // Bouw subregel met **max 4** relevante metadata in prioriteitsvolgorde:
        // 1) aantal + eenheid (indien niet default "stuk")
        // 2) BTW %
        // 3) bestemming (invoice-level, alleen wanneer niet al in description)
        // 4) W-nummer / customer_reference / source_tracking_number
        const details = [];
        // 1) aantal + eenheid
        if (qty > 0) {
          const unitLabel = unit && unit.toLowerCase() !== "stuk" ? unit : "stuk";
          details.push(`${fmtQty(qty)} ${unitLabel}`);
        }
        // 2) BTW %
        if (l.vat_percent !== undefined && l.vat_percent !== null) {
          details.push(`Btw ${Number(l.vat_percent)}%`);
        }
        // 3) bestemming (dedup)
        if (invoice.destination && !includedIn(description, invoice.destination)) {
          details.push(invoice.destination);
        }
        // 4) W-nummer / customer_reference (dedup, kies eerste die er is)
        const wref =
          invoice.source_tracking_number ||
          invoice.customer_reference ||
          null;
        if (wref && !includedIn(description, wref)) {
          details.push(wref);
        }

        return (
          <div
            key={i}
            className="py-3 px-1 sm:px-2"
            data-testid={`invoice-line-card-${i}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4">
              <div className="min-w-0 flex-1">
                <div
                  className="font-chivo font-bold text-sm sm:text-base text-[#082c50] break-words leading-snug"
                  data-testid={`invoice-line-card-${i}-description`}
                >
                  {description}
                </div>
                <div
                  className="text-xs text-gray-500 mt-0.5 font-mono"
                  data-testid={`invoice-line-card-${i}-calc`}
                >
                  {fmtQty(qty)} × {fmtCurrency(unitPrice, currency)}
                </div>
              </div>
              <div
                className="text-right shrink-0"
                data-testid={`invoice-line-card-${i}-total`}
              >
                <div className="font-chivo font-black text-base sm:text-lg text-[#082c50] font-mono whitespace-nowrap">
                  {fmtCurrency(lineTotal, currency)}
                </div>
              </div>
            </div>

            {/* Subregel — subtiel grijs, geen chips */}
            {details.length > 0 && (
              <div
                className="mt-1 text-[11px] text-gray-500 leading-relaxed break-words"
                data-testid={`invoice-line-card-${i}-details`}
              >
                {details.join(" · ")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
